import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScoreGauge } from '../rail/ScoreGauge';

export interface ScoreBreakdownEntry {
  label: string;
  value: number;
}

export interface ScoreBadgeProps {
  score: number;
  grade?: string;
  description?: string;
  breakdown?: ScoreBreakdownEntry[];
  /**
   * Called when the user clicks "Open full Quality panel". When omitted, the
   * default behavior dispatches a `ed-focus-quality` window CustomEvent so
   * the right rail can switch to its Tools tab and scroll Quality into view.
   */
  onOpenFullPanel?: () => void;
  className?: string;
}

/** Window event name dispatched when ScoreBadge's "open full panel" is clicked. */
export const SCORE_FOCUS_EVENT = 'ed-focus-quality';

type Tone = 'success' | 'warning' | 'danger';

const toneFor = (score: number): Tone =>
  score >= 80 ? 'success' : score >= 60 ? 'warning' : 'danger';

const TONE_BG: Record<Tone, string> = {
  success: 'bg-teal-light',
  warning: 'bg-[hsl(var(--status-warning-bg))]',
  danger: 'bg-[hsl(var(--status-error-bg))]',
};

const TONE_BORDER: Record<Tone, string> = {
  success: 'border-teal-dark/25',
  warning: 'border-[hsl(var(--status-warning)/0.3)]',
  danger: 'border-[hsl(var(--status-error)/0.3)]',
};

const TONE_RING: Record<Tone, string> = {
  success: 'stroke-teal-dark',
  warning: 'stroke-[hsl(var(--status-warning))]',
  danger: 'stroke-[hsl(var(--status-error))]',
};

const TONE_TEXT: Record<Tone, string> = {
  success: 'text-teal-dark',
  warning: 'text-[hsl(var(--status-warning))]',
  danger: 'text-[hsl(var(--status-error))]',
};

export function ScoreBadge({
  score,
  grade,
  description,
  breakdown,
  onOpenFullPanel,
  className,
}: ScoreBadgeProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const tone = toneFor(score);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const dashLength = Math.max(0, Math.min(100, score)) / 100 * 47.1;

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Quality score ${score}`}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 pl-1 pr-2.5 rounded-full border cursor-pointer transition-colors',
          TONE_BG[tone],
          TONE_BORDER[tone],
        )}
      >
        <svg width={20} height={20} viewBox="0 0 20 20" className="shrink-0">
          <circle cx={10} cy={10} r={7.5} fill="none" className="stroke-border" strokeWidth={2.5} />
          <circle
            cx={10}
            cy={10}
            r={7.5}
            fill="none"
            className={TONE_RING[tone]}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeDasharray={`${dashLength} 47.1`}
            transform="rotate(-90 10 10)"
          />
        </svg>
        <span className="font-mono text-[12px] font-bold text-foreground">{score}</span>
        <span className="text-[11.5px] font-medium text-muted-foreground">Quality</span>
        <ChevronDown
          className={cn(
            'size-3 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-80 rounded-lg border border-border bg-popover p-3.5 shadow-lg">
          <div className="flex items-center gap-3 mb-2.5">
            <ScoreGauge value={score} size={88} grade={grade ?? null} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold text-foreground">Content quality</div>
              {description && (
                <div className="mt-1 text-[11.5px] leading-snug text-muted-foreground">
                  {description}
                </div>
              )}
            </div>
          </div>
          {breakdown && breakdown.length > 0 && (
            <div
              className="grid gap-1.5 mb-2.5"
              style={{ gridTemplateColumns: `repeat(${Math.min(breakdown.length, 4)}, minmax(0, 1fr))` }}
            >
              {breakdown.map((s) => {
                const t = toneFor(s.value);
                return (
                  <div
                    key={s.label}
                    className="rounded-md bg-warm-bg px-1 py-1.5 text-center"
                  >
                    <div className={cn('font-mono text-[14px] font-bold', TONE_TEXT[t])}>
                      {s.value}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">{s.label}</div>
                  </div>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              if (onOpenFullPanel) {
                onOpenFullPanel();
              } else if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent(SCORE_FOCUS_EVENT));
              }
            }}
            className="w-full rounded-md border border-border bg-warm-bg px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground hover:bg-warm-bg/80 cursor-pointer"
          >
            Open full Quality panel →
          </button>
        </div>
      )}
    </div>
  );
}
